import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Search, Sparkles, ShieldCheck, Zap } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { ProteinCard } from '@/components/Cards';
import { proteinProducts } from '@/data/index';
import { ROUTE_PATHS, type ConstitutionType } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { Link } from 'react-router-dom';

const CATEGORIES: { label: string; value: ConstitutionType | 'all' }[] = [
  { label: '전체보기', value: 'all' },
  { label: '태양인 (風)', value: '태양인' },
  { label: '태음인 (地)', value: '태음인' },
  { label: '소양인 (火)', value: '소양인' },
  { label: '소음인 (水)', value: '소음인' },
];

export default function Products() {
  const [selectedCategory, setSelectedCategory] = useState<ConstitutionType | 'all'>('all');

  const filteredProducts = useMemo(() => {
    if (selectedCategory === 'all') return proteinProducts;
    return proteinProducts.filter((p) => p.targetConstitution === selectedCategory);
  }, [selectedCategory]);

  return (
    <Layout>
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.PROTEIN_INGREDIENTS_5}
            alt="Protein background"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block px-4 py-1.5 mb-6 text-sm font-medium tracking-wider text-primary bg-primary/10 rounded-full">
                PRECISION NUTRITION
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">
                당신의 체질에 최적화된<br />
                <span className="text-primary">한방 단백환 라인업</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                사상체질 이론을 기반으로 설계된 4가지 원소(地·水·火·風) 단백질은
                단순한 영양 공급을 넘어 장부의 균형과 기의 흐름을 조율합니다.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex flex-wrap justify-center gap-3"
            >
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                    selectedCategory === cat.value
                      ? 'bg-primary text-white shadow-lg shadow-primary/25'
                      : 'bg-white border border-border text-muted-foreground hover:border-primary/50 hover:text-primary'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <ProteinCard product={product} />
              </motion.div>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-20">
              <p className="text-muted-foreground">해당 카테고리의 제품을 준비 중입니다.</p>
            </div>
          )}
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto bg-card border border-border rounded-3xl overflow-hidden shadow-xl">
            <div className="grid md:grid-cols-2">
              <div className="p-12 flex flex-col justify-center">
                <h2 className="text-3xl font-bold mb-6">아직 본인의 체질을 모르시나요?</h2>
                <p className="text-muted-foreground mb-8 leading-relaxed">
                  정확한 제품 선택을 위해 AI 기반 체질 진단 시스템을 이용해보세요.
                  8가지 핵심 질문을 통해 당신의 장부 허실과 기의 방향성을 분석하여
                  가장 적합한 단백질을 추천해 드립니다.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to={ROUTE_PATHS.DIAGNOSIS}
                    className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-primary text-white font-bold hover:bg-primary/90 transition-all group"
                  >
                    나의 체질 진단하기
                    <Search className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
              <div className="relative h-64 md:h-auto">
                <img
                  src={IMAGES.HEALTH_CONSULTATION_1}
                  alt="Diagnosis Illustration"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-primary/20 mix-blend-multiply" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center mx-auto mb-6 text-primary">
                <ShieldCheck className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-3">한방 과학의 정밀함</h3>
              <p className="text-muted-foreground text-sm">
                경희대학교 한의학 박사진과 공동 개발한 포뮬러로
                전통 약재와 현대 영양학을 결합했습니다.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center mx-auto mb-6 text-primary">
                <Zap className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-3">최적의 흡수율</h3>
              <p className="text-muted-foreground text-sm">
                체질별 소화 능력을 고려하여 동·식물성 단백질의 비율을
                다르게 배합, 흡수 효율을 극대화했습니다.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center mx-auto mb-6 text-primary">
                <Sparkles className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-3">지속 가능한 관리</h3>
              <p className="text-muted-foreground text-sm">
                단순 섭취를 넘어 전용 앱을 통한 복용 기록과
                체질 반응 피드백 시스템을 제공합니다.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="relative rounded-[2rem] overflow-hidden bg-primary py-20 px-8 text-center text-white">
            <div className="absolute inset-0 opacity-20">
              <img
                src={IMAGES.HERBAL_BOTTLES_3}
                alt="Pattern"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative z-10 max-w-2xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">정기 구독으로 더 건강하게</h2>
              <p className="text-primary-foreground/80 mb-10 text-lg">
                매달 당신의 컨디션 리포트와 함께 배송되는 맞춤 단백환 구독 서비스.
                체질 전문가의 1:1 상담 혜택을 놓치지 마세요.
              </p>
              <button className="px-10 py-4 bg-white text-primary rounded-xl font-bold hover:bg-secondary transition-colors shadow-xl">
                정기 구독 플랜 보기
              </button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
